import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, User, LogOut, LogIn, ChevronDown, ArrowLeft } from 'lucide-react';
import LearnerView from '../leitner/LearnerView';
import TestCenterView from '../leitner/TestCenterView';
import OnboardingTour from '../leitner/OnboardingTour';
import { AuthModal } from '../auth/AuthModal';
import { leitnerService } from '../../services/leitnerService';
import { ProfileModal } from '../shared/ProfileModal';
import { LeitnerManager } from '../leitner/LeitnerManager';
import { useAuth } from '../../contexts/AuthContext';

const LeitnerTemplate = ({ bookId, bookData, customStyling, viewManager }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [mode, setMode] = useState('learner');
  const { viewMode, isAuthenticated, hasManagementAccess } = viewManager || { viewMode: 'public', isAuthenticated: false, hasManagementAccess: false };
  const [loading, setLoading] = useState(true);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  useEffect(() => {
    if (user && bookData) {
      setIsOwner(user.uid === bookData.ownerId);
    }
  }, [user, bookData]);

  useEffect(() => {
    const hasCompletedTour = localStorage.getItem(`leitner_tour_${bookId}`);
    if (!hasCompletedTour && bookData?.leitnerSettings?.enableTourOnFirstVisit !== false) {
      setIsTourOpen(true);
    }
  }, [bookId, bookData]);

  const handleFinishTour = () => {
    localStorage.setItem(`leitner_tour_${bookId}`, 'true');
    setIsTourOpen(false);
  };

  const fetchQuestions = useCallback(async (silent = false) => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      if (!silent) setLoading(true);
      const questionsData = await leitnerService.getQuestionsWithProgress(bookId, user.uid);
      setQuestions(questionsData);
    } catch (error) {
      console.error('Failed to load questions:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [bookId, user]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const updateLearnerProgress = useCallback(async (questionId, isCorrect, isSubQuestion = false, parentQuestionId = null) => {
    if (!user) return;
    
    try {
      // Update progress in Firestore
      await leitnerService.updateLearnerProgress(bookId, user.uid, questionId, isCorrect, isSubQuestion, parentQuestionId);
      
      // Silent refresh in background - doesn't show loading state
      await fetchQuestions(true);
    } catch (error) {
      console.error('Failed to update learner progress:', error);
      throw error;
    }
  }, [bookId, user, fetchQuestions]);

  const batchUpdateProgress = useCallback(async (updates) => {
    if (!user || updates.length === 0) return;
    
    try {
      await leitnerService.batchUpdateLearnerProgress(bookId, user.uid, updates);
      await fetchQuestions(true); // Silent refresh
    } catch (error) {
      console.error('Failed to batch update progress:', error);
    }
  }, [bookId, user, fetchQuestions]);

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
      setQuestions([]);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const styling = {
    primaryColor: bookData?.styling?.primaryColor || '#6366f1',
    backgroundColor: bookData?.styling?.backgroundColor || '#0f172a',
    headerColor: bookData?.styling?.headerColor || '#1e293b',
    headerTextColor: bookData?.styling?.headerTextColor || '#f1f5f9',
    accentColor: bookData?.styling?.accentColor || '#8b5cf6',
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  if (viewMode === 'management' && hasManagementAccess) {
        return (
          <>
            <LeitnerManager
              user={user}
              bookId={bookId}
              bookData={bookData}
              onUpdate={fetchQuestions}
              customStyling={customStyling}
            />
            
          </>
        );
      }  

  const Header = ({ isOwner, bookData, customStyling, navigate }) => {
        const [isSquare, setIsSquare] = useState(true);
    
        const handleImageLoad = (e) => {
          const { naturalWidth, naturalHeight } = e.target;
          setIsSquare(naturalWidth === naturalHeight);
        };
    
        // Get logo height from storeSettings or use default
        const logoHeight = bookData?.logoHeight || 80;
        const isSmall = window.innerWidth < 768;
    
        return (
          <div className="flex items-center gap-2">
            {/* Return Arrow for Album/Book Owner */}
            {isOwner && (
              <button
                onClick={() => navigate("/dashboard")}
                className="p-0 hover:bg-gray-100 rounded-lg transition-colors"
                title="Return to Dashboard"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
            )}
    
            {/* Logo Display with Dynamic Height */}
            {bookData?.logoUrl && (
              <div className="flex-shrink-0">
                <img
                  src={bookData.logoUrl}
                  alt={`${bookData.title || bookData.botName} logo`}
                  style={{
                    height: `${logoHeight}px`,
                    filter: `invert(${bookData?.styling?.logoInvert || 0}%)`,
                    maxWidth: isSmall ? "300px" : "100%",   // limit width on small devices
                    width: "auto",
                  }}
                  className="w-auto object-contain rounded-lg"
                  onLoad={handleImageLoad}
                />
              </div>
            )}
    
            {/* Title Section (only show if logo is square OR no logo at all) */}
            {(isSquare || !bookData?.logoUrl) && (
              <div className="min-w-0">
                <h1
                  className="text-2xl font-bold text-gray-900 truncate"
                  style={{ color: customStyling.primaryColor }}
                >
                  {bookData.title || bookData.botName}
                </h1>
                {bookData.description && (
                  <p className="text-gray-600 mt-1 text-sm line-clamp-2">
                    {bookData.description}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      };

  return (
    <div className="min-h-screen" style={{ backgroundColor: styling.backgroundColor }}>
      {/* Onboarding Tour */}
      {isTourOpen && (
        <OnboardingTour
          isOpen={isTourOpen}
          onFinish={handleFinishTour}
          onClose={handleFinishTour}
          setAppMode={setMode}
        />
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          defaultMode="login"
        />
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal
          onClose={() => setShowProfileModal(false)}
        />
      )}

      <div className="max-w-7xl mx-auto px-2 sm:px-0 lg:px-8 py-4">
        {/* Header */}
        <header className="mb-8 rounded-lg shadow-xl p-2" style={{ backgroundColor: styling.headerColor }}>
          <div className="flex items-center justify-between">
            
            <Header 
                isOwner={isOwner} 
                bookData={bookData} 
                customStyling={styling}
                navigate={navigate}
              />

            {/* User Profile Dropdown */}
            <div className="relative user-menu-container">
              
              {user ? (
                <div>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-0 py-2 rounded-lg hover:bg-slate-700 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center">
                      {user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt={user.displayName || 'User'}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-semibold text-lg">
                          {(user.displayName || user.email || 'U')[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="text-left hidden sm:block">
                      <p className="text-sm font-semibold" style={{ color: styling.headerTextColor }}>
                        {user.displayName || 'Learner'}
                      </p>
                      <p className="text-xs text-slate-400">{user.email}</p>
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} style={{ color: styling.headerTextColor }} />
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-slate-800 rounded-lg shadow-xl border border-slate-700 overflow-hidden z-50">
                      <div className="px-4 py-3 border-b border-slate-700">
                        <p className="text-sm font-semibold text-slate-200">{user.displayName || 'Learner'}</p>
                        <p className="text-xs text-slate-400 truncate">{user.email}</p>
                      </div>
                      <div className="py-2">
                        <button
                          onClick={() => {
                            setShowProfileModal(true);
                            setShowUserMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-700 flex items-center gap-3"
                        >
                          <User className="h-4 w-4" />
                          My Profile
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-slate-700 flex items-center gap-3"
                        >
                          <LogOut className="h-4 w-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium"
                >
                  <LogIn className="h-4 w-4" />
                  Login
                </button>
              )}
            </div>
          </div>

          {/* Navigation Tabs */}
            <nav className="flex justify-center gap-2 mt-2">
              <button
                onClick={() => setMode('learner')}
                className={`px-4 py-2 rounded-lg text-sm transition-all ${
                  mode === 'learner' 
                    ? 'text-white shadow-md' 
                    : 'text-slate-300 hover:bg-slate-700'
                }`}
                style={mode === 'learner' ? { backgroundColor: styling.primaryColor } : {}}
              >
                Study Session
              </button>
              <button
                onClick={() => setMode('test')}
                className={`px-4 py-2 rounded-lg text-sm transition-all ${
                  mode === 'test' 
                    ? 'text-white shadow-md' 
                    : 'text-slate-300 hover:bg-slate-700'
                }`}
                style={mode === 'test' ? { backgroundColor: styling.primaryColor } : {}}
              >
                Test Center
              </button>
            </nav>
        </header>

        <main>
          {!user ? (
            <div className="text-center py-16 bg-slate-800 rounded-lg shadow-xl">
              <User className="h-16 w-16 text-slate-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-200 mb-2">Login Required</h2>
              <p className="text-slate-400 mb-6">Please login to access the Leitner Learning System</p>
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-medium inline-flex items-center gap-2"
              >
                <LogIn className="h-5 w-5" />
                Login Now
              </button>
            </div>
          ) : loading ? (
            <div className="text-center py-16">
              <p className="text-slate-400">Loading questions...</p>
            </div>
          ) : (
            <>
              {mode === 'learner' && (
                <LearnerView 
                  bookId={bookId}
                  questions={questions} 
                  styling={styling}
                  updateLearnerProgress={updateLearnerProgress} 
                />
              )}
              {mode === 'test' && (
                <TestCenterView 
                  questions={questions} 
                  bookId={bookId}
                  batchUpdateProgress={batchUpdateProgress}
                />
              )}
            </>
          )}
        </main>
      </div>

      {/* Floating Settings Button for Owner 
      {isOwner && (
        <button
          onClick={() => navigate(`/books/${bookId}/edit`)}
          className="fixed bottom-6 right-6 p-4 rounded-full shadow-xl hover:shadow-2xl transition-all z-50 text-white"
          style={{ backgroundColor: styling.primaryColor }}
          title="Edit Leitner System"
        >
          <Settings className="w-6 h-6" />
        </button>
      )}
        */}
    </div>
  );
};

export default LeitnerTemplate;